// src/web/routes/_authenticated/trash/index.tsx

import { ArrowUturnLeftIcon, FolderIcon, TrashIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { TRASH_RETENTION_DAYS } from '@shared/constants'
import { createFileRoute } from '@tanstack/react-router'
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'
import { FileInfo } from '@/components/features/FileInfo'
import { SortOrderButton, ToolbarSelect, ViewModeToggle } from '@/components/features/GridToolbar'
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
	Breadcrumb,
	BreadcrumbItem,
	BreadcrumbLink,
	BreadcrumbList,
	BreadcrumbPage,
	BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
import { useAsset } from '@/lib/hooks/useAsset'
import {
	type OrphanWarning,
	TrashFolderGoneError,
	usePurge,
	useRestore,
	useTrash,
	useTrashFolder,
} from '@/lib/hooks/useTrash'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/_authenticated/trash/')({
	component: TrashPage,
	// ?asset=<id> deep link (trashed-asset notification): auto-opens that asset's
	// detail peek once the trash list has loaded.
	validateSearch: (search: Record<string, unknown>): { asset?: string } =>
		typeof search.asset === 'string' && search.asset ? { asset: search.asset } : {},
})

type TrashTarget = { type: 'folder' | 'asset'; id: string; name: string }
type ViewMode = 'grid' | 'list'
type SortBy = 'deleted' | 'name'
type SortOrder = 'asc' | 'desc'
type TypeFilter = 'all' | 'folder' | 'asset'

// Unified row for the merged folders + assets list
interface TrashItem {
	kind: 'folder' | 'asset'
	id: string
	name: string
	deletedAt: string | Date | null
	expiresAt: string | Date | null
	thumbnailUrl: string | null
}

const SORT_OPTIONS = [
	{ value: 'deleted', label: 'Date Deleted' },
	{ value: 'name', label: 'Name' },
] as const

const TYPE_OPTIONS = [
	{ value: 'all', label: 'All Types' },
	{ value: 'folder', label: 'Folders' },
	{ value: 'asset', label: 'Assets' },
] as const

// Defensive: Eden date revival is disabled (api.ts parseDate:false), but older
// SQLite "YYYY-MM-DD HH:MM:SS" UTC strings may still reach us.
function toTime(value: string | Date | null): number {
	if (!value) return 0
	if (value instanceof Date) return value.getTime()
	const ms = new Date(value.includes(' ') ? `${value.replace(' ', 'T')}Z` : value).getTime()
	return Number.isNaN(ms) ? 0 : ms
}

// One formatter instance — toLocaleDateString builds an Intl.DateTimeFormat per call
const DATE_FORMAT = new Intl.DateTimeFormat(undefined, {
	year: 'numeric',
	month: 'short',
	day: 'numeric',
})

function dateLabel(value: string | Date | null): string {
	const ms = toTime(value)
	return ms ? DATE_FORMAT.format(ms) : '—'
}

function expiresLabel(expiresAt: string | Date | null): string {
	const at = toTime(expiresAt)
	if (!at) return ''
	const days = Math.ceil((at - Date.now()) / 86_400_000)
	if (days <= 0) return 'Expires today'
	if (days === 1) return 'Expires in 1 day'
	return `Expires in ${days} days`
}

function TrashPage() {
	// ?asset=<id> deep link from a trashed-asset notification
	const { asset: focusAssetId } = Route.useSearch()
	const autoPeekedRef = useRef(false)

	// Folder drill-in navigation: empty stack = trash root
	const [folderStack, setFolderStack] = useState<{ id: string; name: string }[]>([])
	const currentFolderId = folderStack.length > 0 ? folderStack[folderStack.length - 1].id : null

	// Only one of the two queries is active at a time
	const rootQuery = useTrash(!currentFolderId)
	const folderQuery = useTrashFolder(currentFolderId)
	const activeQuery = currentFolderId ? folderQuery : rootQuery
	const { data, isLoading } = activeQuery
	const liveAssetCount = currentFolderId ? (folderQuery.data?.liveAssetCount ?? 0) : 0

	const restore = useRestore()
	const purge = usePurge()

	const [viewMode, setViewMode] = useState<ViewMode>('grid')
	const [sortBy, setSortBy] = useState<SortBy>('deleted')
	const [sortOrder, setSortOrder] = useState<SortOrder>('desc')
	const [typeFilter, setTypeFilter] = useState<TypeFilter>('all')

	const [purgeTarget, setPurgeTarget] = useState<TrashTarget | null>(null)
	const [orphan, setOrphan] = useState<{ target: TrashTarget; warning: OrphanWarning } | null>(null)

	// Quick-peek panel for asset details. The item is kept while closing so the
	// panel content stays visible during the width-collapse animation; the ref
	// mirrors it so stable mutation callbacks can see the current value.
	const [peekAsset, setPeekAsset] = useState<TrashItem | null>(null)
	const [peekOpen, setPeekOpen] = useState(false)
	const peekAssetRef = useRef<TrashItem | null>(null)
	const peekOpenRef = useRef(false)
	const openPeek = useCallback((item: TrashItem) => {
		peekAssetRef.current = item
		peekOpenRef.current = true
		setPeekAsset(item)
		setPeekOpen(true)
	}, [])
	const closePeek = useCallback(() => {
		peekOpenRef.current = false
		setPeekOpen(false)
	}, [])

	// Esc closes the peek (unless a dialog already consumed the keypress)
	useEffect(() => {
		if (!peekOpen) return
		const onKeyDown = (e: KeyboardEvent) => {
			if (e.key === 'Escape' && !e.defaultPrevented) closePeek()
		}
		window.addEventListener('keydown', onKeyDown)
		return () => window.removeEventListener('keydown', onKeyDown)
	}, [peekOpen, closePeek])

	// Navigating to another folder view closes the peek — its item is no longer on screen
	const openFolder = useCallback(
		(folder: { id: string; name: string }) => {
			closePeek()
			setFolderStack((prev) => [...prev, folder])
		},
		[closePeek]
	)
	const goToStackIndex = useCallback(
		(index: number) => {
			closePeek()
			setFolderStack((prev) => prev.slice(0, index))
		},
		[closePeek]
	)

	const handleRestore = useCallback(
		(target: TrashTarget, confirmOrphan = false) => {
			restore.mutate(
				{ type: target.type, id: target.id, confirmOrphan },
				{
					onSuccess: (res) => {
						if (!res.success && res.orphanWarning) {
							setOrphan({ target, warning: res.orphanWarning })
						} else {
							setOrphan(null)
							if (peekAssetRef.current?.id === target.id) setPeekOpen(false)
							toast.success(`Restored "${target.name}"`)
						}
					},
					onError: (err) => {
						setOrphan(null)
						toast.error(err instanceof Error ? err.message : 'Failed to restore')
					},
				}
			)
		},
		[restore]
	)

	const handlePurge = () => {
		if (!purgeTarget) return
		const target = purgeTarget
		purge.mutate(
			{ type: target.type, id: target.id },
			{
				onSuccess: () => {
					if (peekAssetRef.current?.id === target.id) setPeekOpen(false)
					toast.success(`Permanently deleted "${target.name}"`)
				},
				onError: (err) => toast.error(err instanceof Error ? err.message : 'Failed to delete'),
				onSettled: () => setPurgeTarget(null),
			}
		)
	}

	// Stable per-item handlers so memoized cards only re-render when their own props change
	const handleOpenItem = useCallback(
		(item: TrashItem) => {
			if (item.kind === 'folder') {
				openFolder({ id: item.id, name: item.name })
			} else if (peekOpenRef.current && peekAssetRef.current?.id === item.id) {
				// Clicking the already-peeked asset toggles the panel closed
				closePeek()
			} else {
				openPeek(item)
			}
		},
		[openFolder, openPeek, closePeek]
	)
	const handleRestoreItem = useCallback(
		(item: TrashItem) => handleRestore({ type: item.kind, id: item.id, name: item.name }),
		[handleRestore]
	)
	const handleDeleteItem = useCallback(
		(item: TrashItem) => setPurgeTarget({ type: item.kind, id: item.id, name: item.name }),
		[]
	)

	// Merge folders + assets into one list, then filter and sort
	const { items, totalCount } = useMemo(() => {
		const all: TrashItem[] = [
			...(data?.folders ?? []).map(
				(f): TrashItem => ({
					kind: 'folder',
					id: f.id,
					name: f.name,
					deletedAt: f.deletedAt,
					expiresAt: f.expiresAt,
					thumbnailUrl: null,
				})
			),
			...(data?.assets ?? []).map(
				(a): TrashItem => ({
					kind: 'asset',
					id: a.id,
					name: a.filename ?? 'asset',
					deletedAt: a.dateBinned,
					expiresAt: a.expiresAt,
					thumbnailUrl: a.thumbnailUrl,
				})
			),
		]
		const dir = sortOrder === 'asc' ? 1 : -1
		const filtered = all
			.filter((item) => typeFilter === 'all' || item.kind === typeFilter)
			.map((item) => ({ item, deletedMs: toTime(item.deletedAt) }))
			.sort((a, b) =>
				sortBy === 'name'
					? dir * a.item.name.localeCompare(b.item.name)
					: dir * (a.deletedMs - b.deletedMs)
			)
			.map((e) => e.item)
		return { items: filtered, totalCount: all.length }
	}, [data, typeFilter, sortBy, sortOrder])

	// Deep link: once the trash list loads, open the peek for the focused asset.
	// One-shot (autoPeekedRef) so closing the panel doesn't immediately reopen it.
	useEffect(() => {
		if (autoPeekedRef.current || !focusAssetId) return
		const target = items.find((i) => i.kind === 'asset' && i.id === focusAssetId)
		if (target) {
			autoPeekedRef.current = true
			openPeek(target)
		}
	}, [focusAssetId, items, openPeek])

	const isEmpty = !isLoading && totalCount === 0
	const busy = restore.isPending || purge.isPending

	// Drill-in failed: distinguish "folder genuinely left trash" from transient errors.
	// Both views clear pending dialog state on exit so a stale confirm can't reopen.
	if (currentFolderId && folderQuery.error) {
		const gone = folderQuery.error instanceof TrashFolderGoneError
		const backToRoot = () => {
			setPurgeTarget(null)
			setOrphan(null)
			goToStackIndex(0)
		}
		return (
			<div className="h-full overflow-auto pt-20">
				<div className="px-8 sm:px-14 py-4 sm:py-6">
					<div className="empty-state border-2 border-dashed border-border rounded-2xl">
						<h3 className="empty-state-title">
							{gone ? 'Folder is no longer in trash' : "Couldn't load this folder"}
						</h3>
						<p className="empty-state-description mt-2">
							{gone
								? 'It may have been restored or permanently deleted.'
								: folderQuery.error.message}
						</p>
						<div className="mt-6 flex items-center justify-center gap-3">
							{!gone && (
								<button
									type="button"
									onClick={() => folderQuery.refetch()}
									className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-border bg-background text-sm font-medium text-foreground hover:bg-accent transition-all"
								>
									Try again
								</button>
							)}
							<button
								type="button"
								onClick={backToRoot}
								className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-border bg-background text-sm font-medium text-foreground hover:bg-accent transition-all"
							>
								Back to Trash
							</button>
						</div>
					</div>
				</div>
			</div>
		)
	}

	const peekPanel = (
		<TrashAssetPeek
			item={peekAsset}
			fetchEnabled={peekOpen}
			onClose={closePeek}
			onRestore={() => {
				if (!peekAsset) return
				closePeek()
				handleRestore({ type: 'asset', id: peekAsset.id, name: peekAsset.name })
			}}
			onDelete={() => {
				if (!peekAsset) return
				closePeek()
				setPurgeTarget({ type: 'asset', id: peekAsset.id, name: peekAsset.name })
			}}
			busy={busy}
		/>
	)

	return (
		<div className="flex gap-3 h-full overflow-hidden pt-20 px-8 sm:px-14">
			{/* Main content — scrolls independently of the peek panel */}
			<div className="flex-1 min-w-0 h-full overflow-auto">
				<div className="py-4 sm:py-6">
					{/* Page header */}
					<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 sm:mb-8">
						<div>
							<h1 className="page-title">Trash</h1>
							<p className="page-description">
								Items are permanently deleted {TRASH_RETENTION_DAYS} days after being trashed.
							</p>
						</div>
					</div>

					{/* Breadcrumbs (folder drill-in) */}
					{folderStack.length > 0 && (
						<Breadcrumb className="mb-4">
							<BreadcrumbList>
								<BreadcrumbItem>
									<BreadcrumbLink asChild>
										<button type="button" onClick={() => goToStackIndex(0)}>
											Trash
										</button>
									</BreadcrumbLink>
								</BreadcrumbItem>
								{folderStack.map((f, i) => (
									<BreadcrumbItem key={f.id}>
										<BreadcrumbSeparator />
										{i === folderStack.length - 1 ? (
											<BreadcrumbPage className="truncate max-w-48">{f.name}</BreadcrumbPage>
										) : (
											<BreadcrumbLink asChild>
												<button
													type="button"
													onClick={() => goToStackIndex(i + 1)}
													className="truncate max-w-48"
												>
													{f.name}
												</button>
											</BreadcrumbLink>
										)}
									</BreadcrumbItem>
								))}
							</BreadcrumbList>
						</Breadcrumb>
					)}

					{/* Live synced assets are invisible in trash but affected by a purge */}
					{liveAssetCount > 0 && (
						<p className="mb-4 text-sm text-muted-foreground">
							{liveAssetCount} synced {liveAssetCount === 1 ? 'asset is' : 'assets are'} filed in
							this folder. They can't be trashed, but permanently deleting the folder will remove
							them from it.
						</p>
					)}

					{/* Toolbar: count + filter + sort + view toggle */}
					{!isEmpty && !isLoading && (
						<div className="flex items-center justify-between gap-4 mb-6">
							<div className="text-sm text-muted-foreground">
								<span className="font-medium text-foreground">{items.length.toLocaleString()}</span>
								{items.length === 1 ? ' item' : ' items'}
							</div>

							<div className="flex items-center gap-2">
								<ToolbarSelect
									value={typeFilter}
									onChange={setTypeFilter}
									options={TYPE_OPTIONS}
									ariaLabel="Filter by type"
								/>
								<ToolbarSelect
									value={sortBy}
									onChange={setSortBy}
									options={SORT_OPTIONS}
									ariaLabel="Sort by"
								/>
								<SortOrderButton
									sortOrder={sortOrder}
									onToggle={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
								/>
								<ViewModeToggle viewMode={viewMode} onChange={setViewMode} />
							</div>
						</div>
					)}

					{isLoading && <p className="text-muted-foreground">Loading…</p>}

					{isEmpty && (
						<div className="empty-state border-2 border-dashed border-border rounded-2xl">
							<div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center mb-4">
								<TrashIcon className="w-8 h-8 text-muted-foreground" />
							</div>
							<h3 className="empty-state-title">
								{currentFolderId ? 'Folder is empty' : 'Trash is empty'}
							</h3>
							<p className="empty-state-description mt-2">
								{currentFolderId
									? 'Nothing left inside this folder.'
									: `Deleted folders and assets will appear here for ${TRASH_RETENTION_DAYS} days.`}
							</p>
						</div>
					)}

					{/* Type filter matched nothing (trash itself not empty) */}
					{!isLoading && !isEmpty && items.length === 0 && (
						<p className="py-12 text-center text-sm text-muted-foreground">
							No {typeFilter === 'folder' ? 'folders' : 'assets'} here.
						</p>
					)}

					{items.length > 0 &&
						(viewMode === 'grid' ? (
							<div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-6">
								{items.map((item) => (
									<TrashCard
										key={`${item.kind}-${item.id}`}
										item={item}
										isActive={peekOpen && item.kind === 'asset' && peekAsset?.id === item.id}
										busy={busy}
										onOpenItem={handleOpenItem}
										onRestoreItem={handleRestoreItem}
										onDeleteItem={handleDeleteItem}
									/>
								))}
							</div>
						) : (
							<div className="space-y-1">
								<div className="hidden sm:grid grid-cols-[1fr_90px_140px_140px_160px] gap-4 px-4 py-2 text-xs font-medium text-muted-foreground uppercase tracking-wider border-b border-border">
									<div>Name</div>
									<div>Type</div>
									<div>Deleted</div>
									<div>Expires</div>
									<div className="text-right">Actions</div>
								</div>
								{items.map((item) => (
									<TrashListRow
										key={`${item.kind}-${item.id}`}
										item={item}
										isActive={peekOpen && item.kind === 'asset' && peekAsset?.id === item.id}
										busy={busy}
										onOpenItem={handleOpenItem}
										onRestoreItem={handleRestoreItem}
										onDeleteItem={handleDeleteItem}
									/>
								))}
							</div>
						))}

					{/* Permanent-delete confirm */}
					<AlertDialog open={!!purgeTarget} onOpenChange={(open) => !open && setPurgeTarget(null)}>
						<AlertDialogContent>
							<AlertDialogHeader>
								<AlertDialogTitle>Permanently delete "{purgeTarget?.name}"?</AlertDialogTitle>
								<AlertDialogDescription>
									This cannot be undone.
									{purgeTarget?.type === 'folder'
										? ' The folder and everything inside it will be permanently removed. Synced assets filed in it are not deleted, but they will be removed from the folder.'
										: ' The asset and its files will be permanently removed.'}
								</AlertDialogDescription>
							</AlertDialogHeader>
							<AlertDialogFooter>
								<AlertDialogCancel>Cancel</AlertDialogCancel>
								<AlertDialogAction
									onClick={handlePurge}
									className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
								>
									Delete permanently
								</AlertDialogAction>
							</AlertDialogFooter>
						</AlertDialogContent>
					</AlertDialog>

					{/* Orphan-restore warning */}
					<AlertDialog open={!!orphan} onOpenChange={(open) => !open && setOrphan(null)}>
						<AlertDialogContent>
							<AlertDialogHeader>
								<AlertDialogTitle>Parent is still in trash</AlertDialogTitle>
								<AlertDialogDescription>{orphan?.warning.message}</AlertDialogDescription>
							</AlertDialogHeader>
							<AlertDialogFooter>
								<AlertDialogCancel>Cancel</AlertDialogCancel>
								<AlertDialogAction
									onClick={() => {
										if (orphan) handleRestore(orphan.target, true)
									}}
								>
									Restore anyway
								</AlertDialogAction>
							</AlertDialogFooter>
						</AlertDialogContent>
					</AlertDialog>
				</div>
			</div>

			{/* Asset quick-peek panel — desktop: slides open like the browse filter sidebar */}
			<aside
				className={cn(
					'flex-shrink-0 h-full overflow-hidden transition-all duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]',
					'hidden sm:block',
					peekOpen ? 'w-[380px] py-3' : 'w-0'
				)}
			>
				{peekPanel}
			</aside>

			{/* Mobile: full overlay so asset taps aren't a dead interaction */}
			{peekOpen && (
				<div className="sm:hidden fixed inset-0 z-50 flex justify-end">
					<button
						type="button"
						className="absolute inset-0 bg-black/30 backdrop-blur-sm cursor-default"
						onClick={closePeek}
						aria-label="Close details"
					/>
					<div className="relative h-full w-[88%] max-w-[380px] py-3 pr-3">{peekPanel}</div>
				</div>
			)}
		</div>
	)
}

const CardActions = memo(function CardActions({
	item,
	busy,
	onRestoreItem,
	onDeleteItem,
	className,
}: {
	item: TrashItem
	busy: boolean
	onRestoreItem: (item: TrashItem) => void
	onDeleteItem: (item: TrashItem) => void
	className?: string
}) {
	return (
		<div className={cn('flex items-center gap-2', className)}>
			<button
				type="button"
				onClick={() => onRestoreItem(item)}
				disabled={busy}
				className="flex items-center gap-1 text-xs px-2 py-1 rounded-md bg-muted/60 hover:bg-muted transition-colors disabled:opacity-50"
			>
				<ArrowUturnLeftIcon className="w-3.5 h-3.5" />
				Restore
			</button>
			<button
				type="button"
				onClick={() => onDeleteItem(item)}
				disabled={busy}
				className="flex items-center gap-1 text-xs px-2 py-1 rounded-md text-destructive hover:bg-destructive/10 transition-colors disabled:opacity-50"
			>
				<TrashIcon className="w-3.5 h-3.5" />
				Delete
			</button>
		</div>
	)
})

// Collections-style card — same shell for folders and assets
const TrashCard = memo(function TrashCard({
	item,
	isActive,
	busy,
	onOpenItem,
	onRestoreItem,
	onDeleteItem,
}: {
	item: TrashItem
	isActive: boolean
	busy: boolean
	onOpenItem: (item: TrashItem) => void
	onRestoreItem: (item: TrashItem) => void
	onDeleteItem: (item: TrashItem) => void
}) {
	return (
		<article
			className={cn(
				'group relative rounded-lg border border-border bg-card overflow-hidden',
				'transition-all duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]',
				'hover:shadow-[0_8px_16px_-4px_rgba(0,0,0,0.08),0_4px_8px_-2px_rgba(0,0,0,0.04)]',
				'hover:-translate-y-1 hover:border-primary/50',
				isActive && 'border-primary/60 ring-2 ring-primary/30'
			)}
		>
			<button
				type="button"
				onClick={() => onOpenItem(item)}
				className="block w-full text-left cursor-pointer"
			>
				<div className="aspect-[4/3] relative bg-muted overflow-hidden flex items-center justify-center">
					{item.kind === 'folder' ? (
						<FolderIcon className="w-12 h-12 text-muted-foreground/50" />
					) : item.thumbnailUrl ? (
						<img src={item.thumbnailUrl} alt={item.name} className="w-full h-full object-cover" />
					) : (
						<span className="text-xs text-muted-foreground px-3 text-center break-all line-clamp-3">
							{item.name}
						</span>
					)}
				</div>
			</button>

			<div className="p-4">
				<button
					type="button"
					onClick={() => onOpenItem(item)}
					className="block w-full text-left font-semibold text-[15px] text-foreground truncate hover:text-primary transition-colors"
					title={item.name}
				>
					{item.name}
				</button>
				<div className="flex items-center justify-between mt-3">
					<span className="badge-subtle capitalize">{item.kind}</span>
					<span className="text-xs text-muted-foreground">{expiresLabel(item.expiresAt)}</span>
				</div>
				<CardActions
					item={item}
					busy={busy}
					onRestoreItem={onRestoreItem}
					onDeleteItem={onDeleteItem}
					className="mt-3"
				/>
			</div>
		</article>
	)
})

const TrashListRow = memo(function TrashListRow({
	item,
	isActive,
	busy,
	onOpenItem,
	onRestoreItem,
	onDeleteItem,
}: {
	item: TrashItem
	isActive: boolean
	busy: boolean
	onOpenItem: (item: TrashItem) => void
	onRestoreItem: (item: TrashItem) => void
	onDeleteItem: (item: TrashItem) => void
}) {
	return (
		<div
			className={cn(
				'grid grid-cols-[1fr_auto] sm:grid-cols-[1fr_90px_140px_140px_160px] gap-4 px-4 py-2 items-center rounded-lg transition-colors',
				isActive ? 'bg-primary/10' : 'hover:bg-muted/40'
			)}
		>
			<button
				type="button"
				onClick={() => onOpenItem(item)}
				className="text-left cursor-pointer min-w-0"
			>
				<div className="flex items-center gap-2.5 min-w-0">
					{item.kind === 'folder' ? (
						<div className="w-8 h-8 rounded-md bg-muted/60 flex items-center justify-center flex-shrink-0">
							<FolderIcon className="w-4 h-4 text-muted-foreground" />
						</div>
					) : item.thumbnailUrl ? (
						<img
							src={item.thumbnailUrl}
							alt={item.name}
							className="w-8 h-8 rounded-md object-cover flex-shrink-0"
						/>
					) : (
						<div className="w-8 h-8 rounded-md bg-muted/60 flex-shrink-0" />
					)}
					<span className="truncate text-sm font-medium hover:text-primary">{item.name}</span>
				</div>
			</button>
			<div className="hidden sm:block text-sm text-muted-foreground capitalize">{item.kind}</div>
			<div className="hidden sm:block text-sm text-muted-foreground">
				{dateLabel(item.deletedAt)}
			</div>
			<div className="hidden sm:block text-sm text-muted-foreground">
				{expiresLabel(item.expiresAt)}
			</div>
			<CardActions
				item={item}
				busy={busy}
				onRestoreItem={onRestoreItem}
				onDeleteItem={onDeleteItem}
				className="justify-end"
			/>
		</div>
	)
})

// Right-side peek panel showing full details of a trashed asset. Rendered inside
// a width-animated <aside> (same pattern as the browse filter sidebar), so it
// keeps a fixed inner width to avoid content reflow during the transition.
function TrashAssetPeek({
	item,
	fetchEnabled,
	onClose,
	onRestore,
	onDelete,
	busy,
}: {
	item: TrashItem | null
	fetchEnabled: boolean
	onClose: () => void
	onRestore: () => void
	onDelete: () => void
	busy: boolean
}) {
	// Stop fetching once the panel is closed — cached data still renders the exit animation
	const { data: asset, isLoading } = useAsset(fetchEnabled ? item?.id : undefined)
	const file = asset?.file
	const thumbnail = asset?.thumbnailUrl ?? item?.thumbnailUrl ?? null

	return (
		<div className="h-full w-full sm:w-[380px] flex flex-col rounded-xl border border-border/50 bg-background overflow-hidden">
			{/* Header */}
			<div className="px-5 py-4 flex items-start justify-between gap-3">
				<div className="min-w-0">
					<h2 className="font-medium text-[15px] text-foreground truncate" title={item?.name}>
						{item?.name}
					</h2>
					<p className="text-sm text-muted-foreground">{expiresLabel(item?.expiresAt ?? null)}</p>
				</div>
				<button
					type="button"
					onClick={onClose}
					className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-accent transition-colors flex-shrink-0"
					aria-label="Close details"
				>
					<XMarkIcon className="w-4 h-4" />
				</button>
			</div>

			<div className="flex-1 overflow-y-auto px-5 pb-6 space-y-5">
				{/* Preview */}
				<div className="aspect-video rounded-lg bg-muted overflow-hidden flex items-center justify-center">
					{thumbnail ? (
						<img
							src={thumbnail}
							alt={item?.name ?? 'asset'}
							className="w-full h-full object-contain"
						/>
					) : (
						<span className="text-xs text-muted-foreground px-4 text-center break-all">
							{item?.name}
						</span>
					)}
				</div>

				{/* Details — same component as the asset detail / quick-view panels */}
				{isLoading ? (
					<p className="text-sm text-muted-foreground">Loading details…</p>
				) : (
					<>
						<FileInfo
							file={file ?? null}
							downloadCount={asset?.downloadCount ?? 0}
							dateStaged={asset?.dateStaged ?? null}
							dateLive={asset?.dateLive ?? null}
							uploadedBy={{
								name: asset?.uploadedByName ?? null,
								email: asset?.uploadedByEmail ?? null,
								at: asset?.dateStaged ?? asset?.dateLive ?? null,
							}}
						/>
						<div className="divide-y divide-border/60 border-t border-border/60">
							<PeekRow label="Deleted" value={item ? dateLabel(item.deletedAt) : null} />
							<PeekRow
								label="Deleted by"
								value={asset?.binnedByName ?? asset?.binnedByEmail ?? null}
							/>
							<PeekRow label="Expires" value={item ? expiresLabel(item.expiresAt) : null} />
						</div>
					</>
				)}

				{/* Actions */}
				<div className="flex items-center gap-2 pt-2">
					<button
						type="button"
						onClick={onRestore}
						disabled={busy}
						className="flex-1 flex items-center justify-center gap-1.5 h-9 px-3 rounded-lg bg-muted/60 hover:bg-muted text-sm font-medium transition-colors disabled:opacity-50"
					>
						<ArrowUturnLeftIcon className="w-4 h-4" />
						Restore
					</button>
					<button
						type="button"
						onClick={onDelete}
						disabled={busy}
						className="flex-1 flex items-center justify-center gap-1.5 h-9 px-3 rounded-lg text-destructive hover:bg-destructive/10 text-sm font-medium transition-colors disabled:opacity-50"
					>
						<TrashIcon className="w-4 h-4" />
						Delete permanently
					</button>
				</div>
			</div>
		</div>
	)
}

function PeekRow({ label, value }: { label: string; value: string | null }) {
	if (!value) return null
	return (
		<div className="flex items-start justify-between gap-4 py-2 text-sm">
			<span className="text-muted-foreground flex-shrink-0">{label}</span>
			<span className="text-foreground text-right break-all">{value}</span>
		</div>
	)
}
